import { useState } from 'react'
import { motion } from 'framer-motion'
import { Search, Calendar, Clock, ChevronRight, Tag, BookOpen, ArrowRight, User, Flame } from 'lucide-react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { Link } from 'react-router-dom'
import { mockBlogs, BLOG_CATEGORIES, BLOG_TAGS } from '@/data/news-blogs-data'

const popularPosts = [...mockBlogs].sort((a, b) => parseFloat(b.views) - parseFloat(a.views)).slice(0, 4)

function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function BlogsPage() {
    const [search, setSearch] = useState('')
    const [activeCategory, setActiveCategory] = useState('All')
    const [activeTag, setActiveTag] = useState<string | null>(null)

    const filtered = mockBlogs.filter(b => {
        const matchSearch = b.title.toLowerCase().includes(search.toLowerCase()) || b.excerpt.toLowerCase().includes(search.toLowerCase())
        const matchCat = activeCategory === 'All' || b.category === activeCategory
        const matchTag = !activeTag || b.tags.includes(activeTag)
        return matchSearch && matchCat && matchTag
    })

    const featured = !search && activeCategory === 'All' && !activeTag ? filtered[0] : null
    const restPosts = featured ? filtered.slice(1) : filtered

    return (
        <div className="min-h-screen relative">
            {/* Hero */}
            <section className="relative pt-32 pb-16 overflow-hidden">
                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-dark text-sm font-medium text-primary-blue border border-primary-blue/30 mb-6">
                            <BookOpen className="h-4 w-4" /> Expert Insights
                        </span>
                        <h1 className="text-4xl md:text-6xl font-display font-bold mb-4">
                            <span className="text-theme-primary">NEET Prep</span>
                            <span className="block mt-1 gradient-text">Blog &amp; Resources</span>
                        </h1>
                        <p className="text-lg text-theme-secondary">
                            In-depth guides, strategy breakdowns, success stories, and expert tips from NEET toppers and Future Founder mentors — written for serious aspirants.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Search & Filters */}
            <section className="py-6 sticky top-20 z-30">
                <div className="container mx-auto px-4">
                    <GlassmorphicCard className="p-4">
                        <div className="flex flex-col md:flex-row gap-4 items-center">
                            <div className="relative flex-1 w-full md:w-auto">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-muted" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder="Search blog posts..."
                                    className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary-blue/50"
                                />
                            </div>
                            <div className="flex gap-2 overflow-x-auto pb-1 w-full md:w-auto">
                                {BLOG_CATEGORIES.map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => setActiveCategory(cat)}
                                        className={`shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeCategory === cat
                                            ? 'bg-primary-blue text-white shadow-lg shadow-blue-500/25'
                                            : 'text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800'
                                            }`}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </GlassmorphicCard>
                </div>
            </section>

            <section className="py-10">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Main column */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Featured Post */}
                            {featured && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <Link to={`/blogs/${featured.id}`}>
                                        <GlassmorphicCard className="overflow-hidden group cursor-pointer hover:shadow-xl transition-all duration-300">
                                            <div className="relative h-72 overflow-hidden">
                                                <img
                                                    src={featured.image}
                                                    alt={featured.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                                                <div className="absolute top-4 left-4">
                                                    <span className="px-3 py-1 bg-primary-blue text-white text-xs font-bold rounded-full">
                                                        Featured
                                                    </span>
                                                </div>
                                                <div className="absolute bottom-4 left-4 right-4">
                                                    <span className="text-primary-blue text-xs font-semibold uppercase tracking-wider">{featured.category}</span>
                                                    <h2 className="text-white text-xl font-bold mt-1 leading-tight">{featured.title}</h2>
                                                    <div className="flex items-center gap-3 mt-2 text-white/80 text-xs">
                                                        <span className="flex items-center gap-1"><User className="h-3 w-3" />{featured.author}</span>
                                                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(featured.date)}</span>
                                                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{featured.readTime}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="p-5">
                                                <p className="text-theme-secondary text-sm leading-relaxed mb-4">{featured.excerpt}</p>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex flex-wrap gap-2">
                                                        {featured.tags.slice(0, 3).map(t => (
                                                            <span key={t} className="px-2.5 py-0.5 bg-primary-blue/10 text-primary-blue text-xs rounded-full">{t}</span>
                                                        ))}
                                                    </div>
                                                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary-blue group-hover:gap-2 transition-all">
                                                        Read Post <ArrowRight className="h-4 w-4" />
                                                    </span>
                                                </div>
                                            </div>
                                        </GlassmorphicCard>
                                    </Link>
                                </motion.div>
                            )}

                            {/* Regular Posts */}
                            {restPosts.length === 0 ? (
                                <GlassmorphicCard className="p-12 text-center">
                                    <BookOpen className="h-12 w-12 text-theme-muted mx-auto mb-3" />
                                    <p className="text-theme-secondary">No blog posts found for your filters.</p>
                                </GlassmorphicCard>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    {restPosts.map((post, idx) => (
                                        <motion.div
                                            key={post.id}
                                            initial={{ opacity: 0, y: 15 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: idx * 0.06 }}
                                        >
                                            <Link to={`/blogs/${post.id}`} className="block h-full">
                                                <GlassmorphicCard className="overflow-hidden group cursor-pointer hover:shadow-lg transition-all duration-300 h-full flex flex-col">
                                                    <div className="relative h-44 overflow-hidden">
                                                        <img
                                                            src={post.image}
                                                            alt={post.title}
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                        />
                                                        <div className="absolute top-3 left-3">
                                                            <span className="px-2.5 py-1 bg-white/90 dark:bg-slate-900/90 text-primary-blue text-xs font-semibold rounded-full">
                                                                {post.category}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="p-4 flex flex-col flex-1">
                                                        <h3 className="font-bold text-theme-primary text-sm leading-snug mb-2 group-hover:text-primary-blue transition-colors line-clamp-2">
                                                            {post.title}
                                                        </h3>
                                                        <p className="text-xs text-theme-secondary line-clamp-2 flex-1">{post.excerpt}</p>
                                                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                                                            <div className="flex items-center gap-1 text-xs text-theme-muted">
                                                                <User className="h-3 w-3" />
                                                                <span className="truncate max-w-[100px]">{post.author.split(' ')[0]} {post.author.split(' ')[1] || ''}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2 text-xs text-theme-muted">
                                                                <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{post.readTime}</span>
                                                                <span className="flex items-center gap-1 text-primary-blue font-semibold">
                                                                    Read <ChevronRight className="h-3 w-3" />
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </GlassmorphicCard>
                                            </Link>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            {/* Popular Posts */}
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <Flame className="h-5 w-5 text-orange-500" />
                                    <h3 className="font-bold text-theme-primary">Most Popular</h3>
                                </div>
                                <div className="space-y-4">
                                    {popularPosts.map((post) => (
                                        <Link key={post.id} to={`/blogs/${post.id}`} className="flex gap-3 group cursor-pointer">
                                            <div className="w-16 h-14 rounded-lg overflow-hidden shrink-0">
                                                <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-theme-primary group-hover:text-primary-blue transition-colors line-clamp-2 leading-snug">{post.title}</p>
                                                <p className="text-xs text-theme-muted mt-1">{post.views} views</p>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </GlassmorphicCard>

                            {/* Tags */}
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <Tag className="h-5 w-5 text-primary-blue" />
                                    <h3 className="font-bold text-theme-primary">Popular Tags</h3>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {BLOG_TAGS.map(tag => (
                                        <button
                                            key={tag}
                                            onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${activeTag === tag
                                                ? 'bg-primary-blue text-white shadow-md'
                                                : 'bg-slate-100 dark:bg-slate-800 text-theme-secondary hover:bg-primary-blue/10 hover:text-primary-blue'
                                                }`}
                                        >
                                            {tag}
                                        </button>
                                    ))}
                                </div>
                            </GlassmorphicCard>

                            {/* Categories */}
                            <GlassmorphicCard className="p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <BookOpen className="h-5 w-5 text-primary-blue" />
                                    <h3 className="font-bold text-theme-primary">Categories</h3>
                                </div>
                                <div className="space-y-2">
                                    {BLOG_CATEGORIES.filter(c => c !== 'All').map(cat => {
                                        const count = mockBlogs.filter(b => b.category === cat).length
                                        return (
                                            <button
                                                key={cat}
                                                onClick={() => setActiveCategory(cat)}
                                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${activeCategory === cat
                                                    ? 'bg-primary-blue text-white'
                                                    : 'text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800'
                                                    }`}
                                            >
                                                <span>{cat}</span>
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${activeCategory === cat ? 'bg-white/20' : 'bg-slate-200 dark:bg-slate-700'}`}>{count}</span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </GlassmorphicCard>

                            {/* CTA */}
                            <GlassmorphicCard className="p-5 bg-gradient-to-br from-primary-blue/10 to-blue-600/10 border-primary-blue/20">
                                <h3 className="font-bold text-theme-primary mb-2">Want to Contribute?</h3>
                                <p className="text-sm text-theme-secondary mb-4">Are you a NEET topper or medical expert? Share your knowledge with thousands of aspirants.</p>
                                <Link to="/contact">
                                    <button className="w-full py-2.5 bg-primary-blue hover:bg-blue-600 text-white text-sm font-semibold rounded-xl transition-colors">
                                        Write for Us
                                    </button>
                                </Link>
                            </GlassmorphicCard>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}
